<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaigns.driverList.modal.new.buttonText')"
      @click="openDriverModal = true"
    />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaigns.driverList.modal.specialDriverAssignment.buttonText')"
      @click="openSpecialDriverAssignmentModal = true; selectedCampaignRegistration = undefined"
    />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaigns.driverList.modal.specialDriverCheck.buttonText')"
      @click="openSpecialDriverCheckModal = true"
    />
    <export
      url="/campaign_registrations/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <add-driver-modal v-model:open="openDriverModal" :campaign-group="modelValue" @saved="refetch" />
  <special-driver-assignment-modal
    v-if="modelValue"
    v-model:open="openSpecialDriverAssignmentModal"
    :campaign-group="modelValue"
    :campaign-registration="selectedCampaignRegistration"
    @saved="refetch"
  />
  <special-driver-check-modal v-if="modelValue" v-model:open="openSpecialDriverCheckModal" :campaign-group="modelValue" @saved="refetch" />
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-link="(reg) => ({ name: 'driver-details', params: { driverId: reg?.driver.id } })"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
    :disable-frontend-sorting="true"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, type Ref, unref} from 'vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {
  enumOptionsProvider,
  useProvider,
  withExtraOptions,
} from '@components/utils/table/hooks/provider';
import type {Filter} from '@/components/utils/filter/Filter.vue';
import type {
  Address,
  CampaignGroup,
  CampaignRegistration,
  HydraCollection,
  SpecialDriverAssignment
} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import AddDriverModal from '@pages/campaigns/campaignGroup/AddDriverModal.vue';
import type {Action as ActionType} from '@utils/table/Table.vue';
import {useDelete} from '@hooks/api';
import Export from '@utils/toolbar/panes/Export.vue';
import {useQueryRef} from '@/hooks/url';
import {formatNumberForCurrency, formatTimeDurationFromSeconds} from '@/utils/helpers';
import {format} from 'date-fns';
import SpecialDriverAssignmentModal
  from '@pages/campaigns/campaignGroup/SpecialDriverAssignmentModal.vue';
import SpecialDriverCheckModal from '@pages/campaigns/campaignGroup/SpecialDriverCheckModal.vue';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.driversFromGroup', {
    group: props.modelValue?.name,
  });
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);

const openDriverModal = ref(false);
const openSpecialDriverAssignmentModal = ref(false);
const openSpecialDriverCheckModal = ref(false);
const selectedCampaignRegistration = ref<CampaignRegistration | undefined>(undefined);

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaign.group': props.modelValue?.id ?? '',
  'inArray[campaign.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => {
  const convertedFilter: any = { ...filter.value };
  if (Array.isArray(convertedFilter['specialDriverAssignments.type']) && convertedFilter['specialDriverAssignments.type'].find((item: string) => item === 'none')) {
    delete convertedFilter['specialDriverAssignments.type'];
    return {
      ...fixedFilter.value,
      ...convertedFilter,
      'specialDriverAssignments.type[lte]': '0',
    };
  }
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const [deleteCampaignRegistration, { loading: deleting }] = useDelete();

const actions: ActionType[] = [
  {
    title: t('pages.campaigns.driverList.actions.delete'),
    action: async (item: CampaignRegistration) => {
      if (await deleteCampaignRegistration(`/campaign_registrations/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
  {
    title: t('pages.campaigns.driverList.actions.addSpecialDriverAssignment'),
    action: (item: CampaignRegistration) => {
      selectedCampaignRegistration.value = item;
      openSpecialDriverAssignmentModal.value = true;
    },
  }
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'driver.user.salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    title: t('driver.title'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    visible: true,
    formatter: (v: string) => t(`title.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('driver.title'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'driver.user.phoneNumber',
    title: t('user.phoneNumber'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.phoneNumber'),
      },
    },
  },
  {
    key: 'driver.driverType',
    title: t('driver.driverType.DEFAULT'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverType.DEFAULT'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  {
    key: 'driver.driverTypeAtRegistration',
    title: t('driver.driverTypeAtRegistration'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverTypeAtRegistration'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  {
    key: 'openCredits',
    title: t('driver.openCredits'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: item => item === null ? '-' : formatNumberForCurrency(item),
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.openCredits'),
        icon: <span>CHF</span>,
      },
    },
  },
  {
    key: 'openDebts',
    title: t('driver.openDebts'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: item => item === null ? '-' : formatNumberForCurrency(item),
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.openDebts'),
        icon: <span>CHF</span>,
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaign',
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))
        ),
        label: t('campaign.DEFAULT'),
      },
    },
  },
  {
    key: 'campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'campaign.timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
    },
    title: t('campaign.timeStatus.DEFAULT2'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (cr: CampaignRegistration) => {
      const now = new Date();
      const startDate = new Date(cr.campaign.actualStartDate);
      const endDate = new Date(cr.campaign.actualEndDate);
      if(startDate <= now && endDate >= now) {
        return t('campaign.timeStatus.ongoing');
      } else if(startDate > now) {
        return t('campaign.timeStatus.future');
      } else {
        return t('campaign.timeStatus.past');
      }
    },
    filter: {
      key: 'timeStatus',
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT2'),
      },
    },
  },
  {
    key: 'verificationStatus',
    csvTranslations: {
      "null": t('driver.verificationStatus.null'),
      "Denied": t('driver.verificationStatus.Denied'),
      "Verified": t('driver.verificationStatus.Verified'),
      "NeedsVerification": t('driver.verificationStatus.NeedsVerification'),
    },
    title: t('driver.verificationStatus.DEFAULT'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: false,
    visible: true,
    formatter: (v: string) => v ? t(`driver.verificationStatus.${v}`) : t('driver.verificationStatus.null'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/VerificationStatus', (item) => ({
          label: t(`driver.verificationStatus.${item}`),
          value: item,
        })), [
          { label: t('driver.verificationStatus.null'), value: 'null' },
        ]),
        label: t('driver.verificationStatus.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.verificationRequested',
    title: t('driver.verificationRequested'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.verificationRequested'),
      },
    },
  },
  {
    key: 'driver.liabilityDisclaimerSigned',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driver.liabilityDisclaimerSigned'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.liabilityDisclaimerSigned'),
      },
    },
  },
  {
    key: 'drivenDistance',
    title: t('driver.totalDistance'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        icon: <span>km</span>,
        label: t('driver.totalDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'drivenTime',
    title: t('driver.totalTime') + ' (HH:MM:SS)',
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('driver.totalTime'),
      },
    },
  },
  {
    key: 'kcal',
    title: t('driver.kcal'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    formatter: (v: number) => v.toFixed(2) + ' kcal',
    filter: {
      type: 'range',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        icon: <span>kcal</span>,
        label: t('driver.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('driver.co2'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'kg',
    formatter: (v: number) => v.toFixed(2) + ' kg',
    filter: {
      type: 'range',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        icon: <span>kg</span>,
        label: t('driver.co2'),
      },
    },
  },
  {
    key: 'avgSpeed',
    title: t('driver.avgSpeed'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => `${(v * 3.6).toFixed(2)} Km/h`,
    filter: {
      type: 'range',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        icon: <span>km/h</span>,
        label: t('driver.avgSpeed'),
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'numOfTours',
    title: t('driver.numOfTours'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.numOfTours'),
      },
    },
  },
  {
    key: 'numOfDaysWithTour',
    title: t('driver.numOfDaysWithTour'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.numOfDaysWithTour'),
      },
    },
  },
  {
    key: 'driver.specialDriverPhoto',
    title: t('driver.specialPhoto'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.specialPhoto'),
      },
    }
  },
  {
    key: 'driver.specialDriverStanding',
    title: t('driver.specialStanding'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.specialStanding'),
      },
    }
  },
  {
    key: 'driver.helperDriver',
    title: t('driver.helper'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.helper'),
      },
    },
  },
  {
    key: 'numOfTopRatedPhotos',
    title: t('driver.numOfTopRatedPhotos'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfTopRatedPhotos'),
      },
    },
  },
  {
    key: 'driver.noticeComment',
    title: t('driver.noticeComment.DEFAULT'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    formatter: (v?: string) => v ? t(`driver.noticeComment.${v}`) : '',
    sortable: true,
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/DriverNoticeComment', (v) => ({
          value: v,
          label: t(`driver.noticeComment.${v}`),
        })),
        label: t('driver.noticeComment.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.box.type',
    csvTranslations: {
      null: t('box.status.noBox'),
      v1: t('box.types.v1'),
      v2: t('box.types.v2'),
    },
    title: t('box.types.DEFAULT'),
    group: t('pages.campaigns.driverList.fieldGroups.box'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ?? t('box.status.noBox'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.box'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxType'), [
          { label: t('box.status.noBox'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['driver.box.type'];
            m['exists[driver.box]'] = false;
          } else {
            delete m['exists[driver.box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[driver.box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: t('box.types.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.box.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: t('box.variant.DEFAULT'),
    group: t('pages.campaigns.driverList.fieldGroups.box'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`box.variant.${v}`) : t('box.status.noBox')),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.box'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxVariant', (item) => ({
          value: item,
          label: t(`box.variant.${item}`),
        })), [
          { label: t('box.status.noBox'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['driver.box.variant'];
            m['exists[driver.box]'] = false;
          } else {
            delete m['exists[driver.box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[driver.box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: t('box.variant.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.user.address.street',
    title: t('address.street'),
    group: t('pages.campaigns.driverList.fieldGroups.address'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.driverList.filterGroups.address'),
      options: {
        label: t('address.street'),
      },
    },
  },
  {
    key: 'driver.user.address.cityName',
    title: t('address.city'),
    group: t('pages.campaigns.driverList.fieldGroups.address'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.driverList.filterGroups.address'),
      options: {
        label: t('address.city'),
      },
    },
  },
  {
    key: 'driver.user.address.zip',
    title: t('address.zip'),
    group: t('pages.campaigns.driverList.fieldGroups.address'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.driverList.filterGroups.address'),
      options: {
        label: t('address.zip'),
      },
    },
  },
  {
    key: 'driver.defaultSourceAddress',
    title: `${t('driver.mostFrequentPath')} ${t('from')}`,
    group: t('pages.campaigns.driverList.fieldGroups.frequency'),
    sortable: true,
    sortKey: ['driver.defaultSourceAddress.street', 'driver.defaultSourceAddress.cityName'],
    visible: true,
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.campaigns.driverList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'driver.defaultSourceAddress.street',
              type: 'text',
              group: t('pages.campaigns.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('from')}`,
              },
            },
            {
              key: 'driver.defaultSourceAddress.zip',
              type: 'text',
              group: t('pages.campaigns.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'driver.defaultSourceAddress.cityName',
              type: 'text',
              group: t('pages.campaigns.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.defaultDestinationAddress',
    title: `${t('driver.mostFrequentPath')} ${t('to')}`,
    group: t('pages.campaigns.driverList.fieldGroups.frequency'),
    sortable: true,
    sortKey: ['driver.defaultDestinationAddress.street', 'driver.defaultDestinationAddress.cityName'],
    visible: true,
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.campaigns.driverList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'driver.defaultDestinationAddress.street',
              type: 'text',
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('to')}`,
              },
            },
            {
              key: 'driver.defaultDestinationAddress.zip',
              type: 'text',
              group: t('pages.campaigns.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'driver.defaultDestinationAddress.cityName',
              type: 'text',
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.bicycleType',
    title: t('driver.bicycleType'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
    formatter: (v: string) => v ? t(`driver.bicycleTypes.${v}`) : t('driver.bicycleTypes.null'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Bicycle',(v) => ({
          value: v,
          label: t(`driver.bicycleTypes.${v}`),
        })),
        label: t('driver.bicycleType'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('campaignRegistration.createdAt'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('campaignRegistration.createdAt'),
      },
    },
  },
  {
    key: 'specialDriverAssignments',
    csvTranslations: {
      none: t('campaignRegistration.specialDriverAssignment.type.none'),
      photo: t('campaignRegistration.specialDriverAssignment.type.photo'),
      standing: t('campaignRegistration.specialDriverAssignment.type.standing'),
    },
    title: t('campaignRegistration.specialDriverAssignment.types'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: false,
    visible: true,
    formatter: (v: SpecialDriverAssignment[]|null) => {
      if (v && v.length > 0) {
        return v.map((item) => t(`campaignRegistration.specialDriverAssignment.type.${item.type}`)).join(', ');
      } else {
        return t('campaignRegistration.specialDriverAssignment.type.none');
      }
    },
    filter: {
      type: 'tags',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      key: 'specialDriverAssignments.type',
      options: {
        closeOnSelect: false,
        options: withExtraOptions(enumOptionsProvider('/enums/specialAssignmentType', (v) => ({
          label: t(`campaignRegistration.specialDriverAssignment.type.${v}`),
          value: v,
        })), [
          { label: t('campaignRegistration.specialDriverAssignment.type.none'), value: 'none' },
        ]),
        label: t('campaignRegistration.specialDriverAssignment.types'),
      },
    },
  },
  {
    key: 'driver.endDateLastFinishedCampaign',
    title: t('driver.endDateLastFinishedCampaign'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.endDateLastFinishedCampaign'),
      },
    },
  },
  {
    key: 'driver.endDateLastRegisteredCampaign',
    title: t('driver.endDateLastRegisteredCampaign'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.endDateLastRegisteredCampaign'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<CampaignRegistration>>('/campaign_registrations', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
